/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { Button, Alert, Typography } from "antd";
import { useHistory } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import * as actions from "./actions";
import { CACHE } from "./api";
import RenderTime from "./RenderTime";
import {
	changeFromDate,
	clearCurrentPatient,
	toggleItem,
} from "../Booking/actions";
import { clearPatients } from "../Session/actions";
import MobileDatePicker from "./MobileDatePicker";
import { accountBaseUrl } from "../../helpers/account";
import {
	getAvailabilityLoadingState,
	getAvailabilityRangeLoadingState,
	getAvailability,
} from "./reducers";
import {
	getFromDate,
	hasTimeSelectedForRequests,
	getAvailabilityRequests,
} from "../Booking/reducers";
import moment from "moment";
import { fetchCurrentLocationRequest } from "../Location/actions";
import { getCurrentLocation } from "../Location/reducers";
import { fetchPracticeProfessionalAppointmentReasonsRequest } from "../Service/actions";
import ContentContainer from "../../components/ContentContainer";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { trackEvent } from "utils/analytics";
import usePageView from "../../hooks/usePageView";

const uiFormat = "DD/MM/YYYY";

const Availability = ({
	fromDate,
	availabilityLoadingState,
	match,
	changeFromDate,
	fetchCurrentLocationRequest,
	fetchAvailabilityBetweenRangeRequest,
	currentPractice,
	clearCurrentPatient,
	clearPatients,
	collatedAvailabilityData,
}) => {
	const { params } = match;
	const { locationId: practiceId, practitionerId, reasonId } = params;
	const dispatch = useDispatch();
	const history = useHistory();
  const [selectedMonth, ] = React.useState(moment());

	const standaloneMode = useSelector(
		(state) => state.locations.currentLocation?.standaloneMode,
	);

	const bookingMessageContent = useSelector(
		(state) => state.locations.currentLocation?.bookingMessageContent,
	);
	const bookingMessageTitle = useSelector(
		(state) => state.locations.currentLocation?.bookingMessageTitle,
	);
	const allowDismissableBookingMessage = useSelector(
		(state) => state.locations.currentLocation?.allowDismissableBookingMessage,
	);

	// Track page view with step info
	usePageView("Select Date and Time", 3, "date_time", 60);

	// Store reference to selectNextAvailableDate function from MobileDatePicker
	const selectNextDateRef = React.useRef(null);
	const hasAutoAdvanced = React.useRef(false);

	// Auto-advance to next date if current date has no time slots
	useEffect(() => {
		// Only auto-advance once per date selection, and not while loading
		if (availabilityLoadingState || !fromDate || hasAutoAdvanced.current) return;

		// Check if collated data is loaded and has no availability
		if (collatedAvailabilityData && Array.isArray(collatedAvailabilityData)) {
			const hasAvailability = collatedAvailabilityData.some(item => {
				const availability = item?.availability;
				if (!availability) return false;
				// Check if any time slots exist
				return availability.flat().some(slot => slot && Object.keys(slot).length > 0);
			});

			if (!hasAvailability && selectNextDateRef.current) {
				hasAutoAdvanced.current = true;
				selectNextDateRef.current();
			}
		}
	}, [collatedAvailabilityData, availabilityLoadingState, fromDate]);

	// Reset auto-advance flag when date changes
	useEffect(() => {
		hasAutoAdvanced.current = false;
	}, [fromDate]);

	useEffect(() => {
		CACHE.clear();
		clearCurrentPatient();
		clearPatients();

    const extraDaysBefore = selectedMonth.clone().startOf('month').weekday();
		const extraDaysAfter = (7 - selectedMonth.clone().endOf('month').weekday()) + 7;

		if (practitionerId && reasonId) {
			dispatch(
				toggleItem({
					reasonId: Number(reasonId),
					practitionerId: Number(practitionerId),
				}),
			);
		}
		fetchAvailabilityBetweenRangeRequest(
      practiceId,
      selectedMonth.clone().startOf('month').subtract(extraDaysBefore, "days").format(uiFormat),
			selectedMonth.clone().endOf('month').add(extraDaysAfter, "days").format(uiFormat)
    );

		fetchCurrentLocationRequest(practiceId);
	}, []);

	const handleDateChange = React.useCallback((id, date) => {
		changeFromDate(id, date);
		trackEvent("date_selected", { date });
	}, []);

	const handleGoBack = React.useCallback(() => {
		if (history.length > 1) {
			return history.goBack();
		} else if (practitionerId) {
			return history.push(
				`${accountBaseUrl}/locations/${practiceId}/practitioner/${practitionerId}/reasons`,
			);
		} else {
			return history.push(`${accountBaseUrl}/locations/${practiceId}/reasons`);
		}
	}, []);

	return (
		<>
			<ContentContainer
				showNext={false}
				step={3}
				themeColor={currentPractice?.themeColor}
				stepPercentage={60}
				pageTitle="Select suitable date and time"
				backButton={
					<Button
						onClick={() => handleGoBack()}
						shape="round"
						type="text"
						icon={<ArrowLeftOutlined />}
					/>
				}
			>
					<div style={{ marginBottom: 16 }}>
					<MobileDatePicker
						id={practiceId}
						fromDate={fromDate}
						changeFromDate={handleDateChange}
						practiceId={practiceId}
						themeColor={currentPractice?.themeColor}
						leadTimeToBookInDays={currentPractice?.leadTimeToBookInDays}
						howFarCanBookInDays={currentPractice?.howFarCanBookInDays}
						onSelectNextDate={(fn) => { selectNextDateRef.current = fn; }}
					/>
				</div>

				<RenderTime
					practiceId={practiceId}
					date={fromDate}
					themeColor={currentPractice?.themeColor}
				/>

				{!standaloneMode && bookingMessageContent && (
					<Alert
						icon={<InfoCircleOutlined />}
						style={{
							background: "#fff",
							border: "1px solid #d9d9d9",
							marginTop: 16,
						}}
						showIcon
						message={
							<Typography.Text>{bookingMessageTitle}</Typography.Text>
						}
						description={bookingMessageContent}
						closable={allowDismissableBookingMessage}
					/>
				)}
			</ContentContainer>
		</>
	);
};

const mapStateToProps = (state) => ({
	availabilityLoadingState: getAvailabilityLoadingState(state.availability),
	availabilityRangeLoadingState: getAvailabilityRangeLoadingState(
		state.availability,
	),
	isTimeSlotSelected: hasTimeSelectedForRequests(state.booking),
	fromDate: getFromDate(state.booking),
	availabilityRequests: getAvailabilityRequests(state.booking),
	currentPractice: getCurrentLocation(state.locations),
	collatedAvailabilityData: getAvailability(state.availability),
});

export default connect(mapStateToProps, {
	...actions,
	changeFromDate,
	fetchCurrentLocationRequest,
	fetchPracticeProfessionalAppointmentReasonsRequest,
	clearCurrentPatient,
	clearPatients,
})(Availability);
